import { isApiError } from '@/api/http/errors';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { PropertyRuntime } from '../property-queries';
import { actor, allowed, commandOwner, epoch, freezeRequest, publish } from './command-owner';
export function useCurrentCommand<T, B>({
  runtime,
  scope,
  readKey,
  readEnabled = true,
  bits,
  domain = 'property',
  read,
  write,
  validate,
  onActorChanged,
}: {
  runtime: PropertyRuntime;
  scope: string;
  readKey?: string;
  readEnabled?: boolean;
  bits: number;
  /** 권한 비트를 읽을 업무 영역. 기본은 property. */
  domain?: string;
  read: () => Promise<T>;
  write: (b: B) => Promise<void>;
  validate: (body?: B) => Promise<void>;
  onActorChanged?: () => void;
}) {
  const owner = commandOwner(runtime, scope);
  useSyncExternalStore(
    (l) => {
      owner.listeners.add(l);
      return () => {
        owner.listeners.delete(l);
      };
    },
    () => owner.version,
  );
  const [current, setCurrent] = useState<T>();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const currentActor = useRef<string | undefined>(undefined);
  const discardActorDraft = () => {
    currentActor.current = undefined;
    owner.attempt = null;
    setCurrent(undefined);
    publish(owner);
    onActorChanged?.();
  };
  const active = useRef({ owner, readKey, readEnabled });
  active.current = { owner, readKey, readEnabled };
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const valid = (s: string, e: string) =>
    mounted.current &&
    active.current.owner === owner &&
    active.current.readKey === readKey &&
    scope === s &&
    epoch(runtime) === e &&
    allowed(runtime, bits, domain);
  const readEpoch = useRef(0);
  const refresh = async () => {
    if (!active.current.readEnabled) return;
    const s = scope,
      e = epoch(runtime);
    const generation = ++owner.readGeneration;
    const startedReadEpoch = readEpoch.current;
    const acceptedAttempt = owner.attempt?.accepted ? owner.attempt : null;
    const currentRead = () =>
      active.current.readEnabled &&
      startedReadEpoch === readEpoch.current &&
      valid(s, e) &&
      generation === owner.readGeneration;
    setError('');
    setSuccess(false);
    try {
      if (!allowed(runtime, bits, domain)) return;
      const a = await actor(runtime, bits, domain);
      if (!currentRead()) return;
      if (currentActor.current && currentActor.current !== a) {
        discardActorDraft();
        throw new Error('작업자가 변경되어 초안을 폐기했습니다. 현재값을 다시 조회해 주세요.');
      }
      await validate();
      if (!currentRead()) return;
      const value = await read();
      if (!currentRead()) return;
      const after = await actor(runtime, bits, domain);
      if (!currentRead()) return;
      if (a !== after || (owner.attempt && owner.attempt.actor !== after)) {
        discardActorDraft();
        throw new Error('작업자가 변경되었습니다.');
      }
      setCurrent(value);
      currentActor.current = after;
      if (acceptedAttempt && owner.attempt === acceptedAttempt) {
        owner.attempt = null;
        publish(owner);
        setSuccess(true);
      }
    } catch (err) {
      if (currentRead()) {
        if (isApiError(err) && [401, 403].includes(err.status ?? 0)) {
          owner.attempt = null;
          setCurrent(undefined);
          publish(owner);
          if (err.status === 403)
            runtime.session.reportDomainAccessDenied(
              domain,
              runtime.session.getSnapshot().generation,
            );
        }
        setError(errorMessage(err));
      }
    }
  };
  useEffect(() => {
    setCurrent(undefined);
    currentActor.current = undefined;
    setError('');
    setSuccess(false);
    if (readEnabled) void refresh(); // current values belong to the selected scope and authorization epoch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, readKey, owner, owner.epoch]);
  const previousReadEnabled = useRef(readEnabled);
  useEffect(() => {
    const resumed = readEnabled && !previousReadEnabled.current;
    previousReadEnabled.current = readEnabled;
    if (!readEnabled) readEpoch.current++;
    if (resumed && (!current || owner.attempt?.accepted)) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readEnabled]);
  const submit = async (body?: B) => {
    if (owner.busy || !allowed(runtime, bits, domain)) return;
    if (owner.attempt?.accepted) {
      await refresh();
      return;
    }
    const s = scope,
      e = epoch(runtime);
    owner.readGeneration++;
    owner.busy = true;
    publish(owner);
    setError('');
    setSuccess(false);
    let sent = false;
    try {
      const a = await actor(runtime, bits, domain);
      if (!valid(s, e)) return;
      if (owner.attempt && owner.attempt.actor !== a) {
        discardActorDraft();
        throw new Error('작업자가 변경되어 이전 요청을 폐기했습니다.');
      }
      if (currentActor.current && currentActor.current !== a) {
        discardActorDraft();
        throw new Error('작업자가 변경되어 초안을 폐기했습니다. 현재값을 다시 조회해 주세요.');
      }
      await validate((owner.attempt?.body as B | undefined) ?? body);
      if (!valid(s, e)) return;
      if (!owner.attempt) {
        if (!body) return;
        owner.attempt = { body: freezeRequest(structuredClone(body)), actor: a, accepted: false };
        publish(owner);
      }
      const attempt = owner.attempt;
      const before = await actor(runtime, bits, domain);
      if (!valid(s, e)) return;
      if (before !== a) {
        discardActorDraft();
        throw new Error('작업자가 변경되었습니다.');
      }
      sent = true;
      await write(attempt.body as B);
      if (owner.attempt === attempt && !attempt.accepted) {
        attempt.accepted = true;
        // Fence reads begun before acceptance, including another mounted consumer's read.
        owner.readGeneration++;
        publish(owner);
      }
      if (!valid(s, e)) return;
      await refresh();
    } catch (err) {
      if (valid(s, e)) {
        if (isApiError(err) && [401, 403, 404, 409, 422].includes(err.status ?? 0)) {
          owner.attempt = null;
          setCurrent(undefined);
          if (err.status === 403)
            runtime.session.reportDomainAccessDenied(
              domain,
              runtime.session.getSnapshot().generation,
            );
        } else if (!sent) owner.attempt = null;
        setError(errorMessage(err));
        publish(owner);
      }
    } finally {
      if (owner.epoch === e) {
        owner.busy = false;
        publish(owner);
      }
    }
  };
  return {
    current,
    error,
    success,
    refresh,
    submit,
    busy: owner.busy,
    attempt: owner.attempt,
    canWrite: allowed(runtime, bits, domain),
  };
}
export function errorMessage(e: unknown) {
  return isApiError(e)
    ? e.status === 409
      ? '현재값이 변경되었습니다. 다시 조회하고 비교·확인해 주세요.'
      : e.status === 422
        ? '입력값을 확인해 주세요. 이름·문구·구성요소와 변경 사유를 다시 검토하세요.'
        : e.status === 404
          ? '현재 대상 또는 기능을 찾을 수 없습니다.'
          : e.status === 401 || e.status === 403
            ? '현재 작업자의 권한을 다시 확인해 주세요.'
            : e.status === 503
              ? '현재 서비스에서 자료를 확인할 수 없습니다. 다시 조회해 주세요.'
              : '요청 결과를 확인하지 못했습니다. 원래 요청을 보존합니다.'
    : e instanceof Error
      ? e.message
      : '현재 자료를 확인하지 못했습니다.';
}
